import { useEffect, useMemo, useState, useRef } from 'react';
import { useDisclosure, useViewportSize } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import {
	MantineReactTable,
	useMantineReactTable,
	MRT_ShowHideColumnsButton,
	MRT_ToggleGlobalFilterButton,
	MRT_ToggleFiltersButton,
} from 'mantine-react-table';
import { MRT_Localization_ES } from 'mantine-react-table/locales/es';
import { MRT_FilterFns } from 'mantine-react-table';
import { Alert, Group, Stack, ActionIcon, Tooltip, Menu } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Button, Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap';
import { IconRefresh, IconTableExport } from '@tabler/icons-react';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';
import { generateExcel } from '../../helpers/generateExcel';
import { toSmartTitleCase } from '../../utils/toSmartTitleCase';
import { CustomFilterSelect } from './CustomFilterSelect';
import { useFetchRemoteData } from '../Hooks/useFetchRemoteData';
import { useDispatch } from 'react-redux';
import { isEqual } from 'lodash';

// active plugins dayjs
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);

/**
 * CONFIGURACIÓN DE COLUMNAS
 *
 * @typedef {Object} ColumnConfig
 * @property {string} header - Título de la columna que se mostrará en el encabezado
 * @property {string} accessorKey - Clave para acceder al valor en el objeto de datos
 * @property {Function} [accessorFn] - Función personalizada para obtener el valor de la celda
 * @property {Function} [Cell] - Componente personalizado para renderizar la celda
 * @property {'string'|'number'|'boolean'|'date'|'datetime'|'separator'|'select'|'multi-select'|'float.N'} [dataType='string'] - Tipo de dato de la columna
 * @property {boolean} [visible=true] - Si la columna es visible por defecto
 * @property {string|number} [width] - Ancho de la columna (px o %)
 * @property {number} [size] - Tamaño de la columna en píxeles
 * @property {'left'|'center'|'right'} [align] - Alineación del contenido
 * @property {'asc'|'desc'} [defaultSort] - Ordenamiento por defecto
 * @property {boolean} [summary] - Si mostrar suma en el footer
 * @property {boolean} [uppercase] - Convertir texto a mayúsculas
 * @property {boolean} [lowercase] - Convertir texto a minúsculas
 * @property {boolean} [titlecase] - Convertir texto a formato título
 *
 * TIPOS DE DATOS SOPORTADOS:
 *
 * - 'string': Texto normal con filtro de texto
 * - 'number': Números con filtro de rango
 * - 'boolean': Valores booleanos con checkbox
 * - 'date': Fechas con filtro de rango de fechas (formato DD-MM-YYYY)
 * - 'datetime': Fechas con hora con filtro de rango (formato DD-MM-YYYY HH:mm)
 * - 'separator': Números con separadores de miles (formato 1.234,56)
 * - 'select': Lista desplegable para filtrado
 * - 'multi-select': Lista desplegable múltiple
 * - 'float.N': Números decimales con N decimales (ej: float.2 para 2 decimales)
 *
 * @example
 * const columns = [
 *   {
 *     header: 'ID',
 *     accessorKey: 'id',
 *     dataType: 'number',
 *     width: '80px',
 *     visible: false
 *   },
 *   {
 *     header: 'Última Conexión',
 *     accessorKey: 'lastLogin',
 *     dataType: 'datetime',
 *     defaultSort: 'desc'
 *   },
 *   {
 *     header: 'Estado',
 *     accessorKey: 'status',
 *     dataType: 'multi-select'
 *   },
 *   {
 *     header: 'Activo',
 *     accessorKey: 'isActive',
 *     dataType: 'boolean'
 *   },
 *   {
 *     header: 'Precio',
 *     accessorKey: 'price',
 *     dataType: 'float.2',
 *     align: 'right'
 *   },
 *   {
 *     header: 'Nombre Personalizado',
 *     accessorFn: (row) => `${row.firstName} ${row.lastName}`,
 *     dataType: 'string',
 *     titlecase: true,
 * 	 Cell: ({ cell }) => {
 * 		 return <Badge color='blue'>{cell.getValue()}</Badge>;
 * 	 }
 *   }
 * ];
 */

/**
 * ODataGrid - Componente de tabla avanzada con soporte para operaciones locales y remotas
 *
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Array} [props.baseFilters] - Filtros base que se aplicarán siempre a la consulta remota
 * @param {Function} props.clearCacheFunc - Función para limpiar el cache
 * @param {Array} props.columns - Configuración de columnas de la tabla
 * @param {boolean} [props.columnOrdering=false] - Permite reordenar columnas arrastrando
 * @param {Array} [props.data] - Datos para modo local. Si se proporciona y remoteOperations es false, se usa modo local
 * @param {number} [props.defaultPageSize=50] - Tamaño de página por defecto
 * @param {Function} [props.enableRowSelection] - Función para determinar si una fila es seleccionable
 * @param {boolean} [props.enableStickyFooter=true] - Hace que el footer sea sticky
 * @param {boolean} [props.enableStickyHeader=true] - Hace que el header sea sticky
 * @param {boolean} [props.filtering=true] - Habilita el filtrado de columnas
 * @param {Function} props.getDataFunc - Función para obtener datos remotos
 * @param {Function} [props.getRowIdTable] - Función para obtener el ID único de cada fila
 * @param {boolean} [props.isLoadingData=false] - Indica si los datos están cargando (modo local)
 * @param {Object} [props.mantineTableContainerProps={ sx: { maxHeight: '500px' } }] - Props para el contenedor de la tabla
 * @param {Function} [props.masterDetailComponent] - Componente para mostrar detalles expandibles de fila
 * @param {string} [props.nameFile] - Nombre del archivo para exportación a Excel
 * @param {string} [props.queryFields] - Campos específicos a consultar en el backend
 * @param {string} [props.queryPopulate] - Relaciones a poblar en la consulta del backend
 * @param {boolean} [props.refetchRemoteData] - Trigger para refrescar datos remotos
 * @param {Object|boolean} [props.remoteOperations=false] - Configuración de operaciones remotas
 * @param {boolean} [props.remoteOperations.filtering] - Habilita filtrado remoto
 * @param {boolean} [props.remoteOperations.pagination] - Habilita paginación remota
 * @param {boolean} [props.remoteOperations.sorting] - Habilita ordenamiento remoto
 * @param {boolean} [props.searching=true] - Habilita búsqueda global
 * @param {string} [props.selectAllMode] - Modo de selección múltiple ('all', 'page', etc.)
 * @param {Object} [props.selection] - Objeto de control de selección
 * @param {Object} [props.selection.rowSelection] - Estado actual de filas seleccionadas
 * @param {Function} [props.selection.setRowSelection] - Función para actualizar filas seleccionadas
 * @param {boolean} [props.sorting=true] - Habilita ordenamiento de columnas
 * @param {string} [props.theme='default'] - Tema de la tabla ('default', 'minimal')
 * @param {boolean} [props.withColumnBorders=true] - Muestra bordes entre columnas
 * @param {boolean} [props.formatHeaders=true] - Aplica toSmartTitleCase a los títulos de las columnas
 * @param {Function|Array} [props.customToolbarActions] - Acciones personalizadas para el toolbar superior. Puede ser una función que retorna JSX o un array de objetos de configuración de botones
 * @param {string} [props.exportDateField='creation_date'] - Campo de fecha a usar en el filtro de exportación a Excel. Por defecto 'creation_date'. Usar 'register_date' para entidades donde la fecha relevante es la fecha de registro.
 *
 * @example
 * // Uso básico con datos locales
 * <ODataGrid
 *   data={localData}
 *   columns={[
 *     { header: 'Nombre', accessorKey: 'name', dataType: 'string' },
 *     { header: 'Edad', accessorKey: 'age', dataType: 'number' }
 *   ]}
 * />
 *
 * @example
 * // Uso con operaciones remotas
 * <ODataGrid
 *   getDataFunc={fetchDataFromAPI}
 *   columns={columns}
 *   remoteOperations={{
 *     pagination: true,
 *     sorting: true,
 *     filtering: true
 *   }}
 *   baseFilters={{ status: { $eq: 'active' } }}
 * />
 *
 * @example
 * // Uso con botones personalizados del toolbar (array de configuraciones)
 * <ODataGrid
 *   columns={columns}
 *   getDataFunc={fetchDataFromAPI}
 *   customToolbarActions={[
 *     {
 *       label: 'Crear Nuevo',
 *       icon: Icons.plus,
 *       className: 'btn btn-label btn-primary',
 *       onClick: () => handleCreate(),
 *     },
 *     {
 *       label: 'Importar',
 *       icon: 'ri-upload-line',
 *       className: 'btn btn-label btn-secondary',
 *       onClick: () => handleImport(),
 *       disabled: !canImport
 *     }
 *   ]}
 * />
 *
 * @example
 * // Uso con función personalizada para el toolbar
 * <ODataGrid
 *   columns={columns}
 *   getDataFunc={fetchDataFromAPI}
 *   customToolbarActions={({ table }) => (
 *     <div className="d-flex gap-2">
 *       <button onClick={() => handleCustomAction(table)}>
 *         Acción Personalizada
 *       </button>
 *     </div>
 *   )}
 * />
 *
 * @example
 * // Configuración de columnas avanzada
 * const columns = [
 *   {
 *     header: 'Nombre',
 *     accessorKey: 'name',
 *     dataType: 'string',
 *     uppercase: true,
 *     visible: true,
 *     width: '200px'
 *   },
 *   {
 *     header: 'Precio',
 *     accessorKey: 'price',
 *     dataType: 'separator',
 *     summary: true,
 *     align: 'right'
 *   },
 *   {
 *     header: 'Fecha',
 *     accessorKey: 'date',
 *     dataType: 'datetime',
 *     defaultSort: 'desc'
 *   }
 * ];
 *
 * @returns {JSX.Element} Componente de tabla renderizado
 */
export const ODataGrid = props => {
	const { height } = useViewportSize();
	const {
		baseFilters,
		clearCacheFunc,
		columns,
		columnOrdering = false,
		customToolbarActions,
		data,
		defaultPageSize = 50,
		enableRowSelection,
		enableStickyFooter = true,
		enableStickyHeader = true,
		exportDateField = 'creation_date',
		filtering = true,
		getDataFunc,
		getRowIdTable,
		isLoadingData = false,
		mantineTableContainerProps = {
			style: { 
				maxHeight: '54vh',
				overflowY: 'auto'
			}
		},
		masterDetailComponent,
		nameFile,
		queryFields,
		queryPopulate,
		refetchRemoteData,
		remoteOperations = false,
		searching = true,
		selectAllMode,
		selection,
		sorting = true,
		theme = 'default',
		withColumnBorders = true,
		formatHeaders = true,
		...extraProps
	} = props;

	const {
		onPaginationChange: onPaginationChangeFromParent,
		...tableExtraProps
	} = extraProps;

	const [tableMode] = useState(
		data && remoteOperations === false ? 'local' : 'remote'
	);

	const [columnVisibilityList, setColumnVisibilityList] = useState({});
	const [density, setDensity] = useState('xs');
	const [filteringList, setFilteringList] = useState([]);
	const [globalFilter, setGlobalFilter] = useState('');
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: defaultPageSize,
	});
	const [query, setQuery] = useState();
	const [sortingList, setSortingList] = useState([]);

	// Track previous filter/sort values to detect real changes
	const prevFiltersRef = useRef([]);
	const prevSortingRef = useRef([]);

	// Memoize searchable columns for global filter optimization
	const searchableColumns = useMemo(() => {
		return columns
			.filter(column => column.accessorKey)
			.map(column => column.accessorKey);
	}, [columns]);

	// Memoize remote operations flags to avoid unnecessary re-renders
	const remoteFlags = useMemo(
		() => ({
			filtering: remoteOperations?.filtering || false,
			pagination: remoteOperations?.pagination || false,
			sorting: remoteOperations?.sorting || false,
		}),
		[remoteOperations]
	);

	const {
		data: remoteData,
		error,
		isLoading,
		isFetching,
		refetch,
	} = useFetchRemoteData(getDataFunc, {
		query,
		skip: tableMode === 'local',
	});

	useEffect(() => {
		if (refetchRemoteData) {
			refetch();
		}
	}, [refetchRemoteData]);

	/**
	 * Consolidated useEffect for query construction with smart pagination reset
	 *
	 * Flow when filters/sorting change:
	 * 1. Detect real changes (not just MRT reference changes)
	 * 2. If changed and not on page 1 → reset pagination and exit (prevents double query)
	 * 3. Next render with pageIndex: 0 → build query normally
	 * Result: ONE API call per real change instead of two
	 */
	useEffect(() => {
		if (tableMode === 'local') return;

		// Initialize refs on first render
		if (
			prevFiltersRef.current.length === 0 &&
			prevSortingRef.current.length === 0
		) {
			prevFiltersRef.current = filteringList;
			prevSortingRef.current = sortingList;
		}

		// Check if filters/sorting actually changed (deep comparison)
		const filtersChanged = !isEqual(filteringList, prevFiltersRef.current);
		const sortingChanged = !isEqual(sortingList, prevSortingRef.current);
		const shouldResetPagination = filtersChanged || sortingChanged;

		// If filters/sorting changed and we're not on page 1, reset pagination and exit
		// This prevents building the query twice
		if (shouldResetPagination && pagination.pageIndex !== 0) {
			setPagination(prev => ({ ...prev, pageIndex: 0 }));
			// Update refs for next comparison
			prevFiltersRef.current = filteringList;
			prevSortingRef.current = sortingList;
			return;
		}

		let queryObj = {};

		// Handle queryPopulate and queryFields
		if (queryPopulate) queryObj.populate = queryPopulate;
		if (queryFields) queryObj.fields = queryFields;

		// Handle sorting
		if (remoteFlags.sorting && sortingList.length) {
			queryObj.sort = [
				`${sortingList[0]?.id}:${sortingList[0]?.desc ? 'desc' : 'asc'}`,
			];
		}

		// Handle column filters
		if (remoteFlags.filtering && filteringList.length) {
			queryObj.filters = {};

			filteringList.forEach(item => {
				const itemKey = item.id;
				const itemColumn = columns.find(
					col => col.accessorKey === itemKey || col.id === itemKey
				);
				const dataType = itemColumn?.dataType || 'string';
				const value = item.value;

				// Text: $containsi (case-insensitive contains)
				if (
					dataType === 'string' &&
					typeof value === 'string' &&
					value.trim() !== ''
				) {
					queryObj.filters[itemKey] = { $containsi: value };
					return;
				}

				// Number / float range: $between
				if (
					(dataType === 'number' || String(dataType).startsWith('float')) &&
					Array.isArray(value) &&
					value.length === 2
				) {
					queryObj.filters[itemKey] = { $between: value };
					return;
				}

				// Boolean: $eq
				if (dataType === 'boolean') {
					queryObj.filters[itemKey] = { $eq: value };
					return;
				}

				// Date range: $between or $gte or $lte
				if (
					dataType === 'date' ||
					(dataType === 'datetime' &&
						Array.isArray(value) &&
						value.length === 2)
				) {
					if (value[0] && value[1]) {
						queryObj.filters[itemKey] = { $between: value };
					} else if (value[0]) {
						queryObj.filters[itemKey] = { $gte: value[0] };
					} else if (value[1]) {
						queryObj.filters[itemKey] = { $lte: value[1] };
					}
					return;
				}

				// Select: $eqi (case-insensitive exact match)
				if (dataType === 'select' && value) {
					queryObj.filters[itemKey] = { $eqi: value };
					return;
				}

				// Multi-select: $in
				if (dataType === 'multi-select') {
					if (Array.isArray(value) && value.length > 0) {
						queryObj.filters[itemKey] = { $in: value };
					}
					return;
				}

				// Fallback: $eq for numbers, $containsi for strings
				if (!isNaN(value)) {
					queryObj.filters[itemKey] = { $eq: Number(value) };
				} else if (typeof value === 'string' && value.trim() !== '') {
					queryObj.filters[itemKey] = { $containsi: value };
				}
			});
		}

		// Handle baseFilters - merge them with existing filters
		if (baseFilters) {
			queryObj.filters = {
				...queryObj.filters,
				...baseFilters,
			};
		}

		// Handle global filter
		if (globalFilter) {
			queryObj.filters = {
				...queryObj.filters,
				$or: searchableColumns.map(accessorKey => ({
					[accessorKey]: { $containsi: globalFilter },
				})),
			};
		}

		// Handle pagination
		if (remoteFlags.pagination) {
			queryObj.pagination = {
				page: pagination.pageIndex + 1,
				pageSize: pagination.pageSize,
			};
		}

		// Only update query if it actually changed (deep comparison)
		if (!isEqual(queryObj, query)) {
			setQuery(queryObj);
		}

		// Update refs with current values for next comparison (always at the end)
		prevFiltersRef.current = filteringList;
		prevSortingRef.current = sortingList;
	}, [
		tableMode,
		sortingList,
		filteringList,
		pagination,
		baseFilters,
		globalFilter,
		queryPopulate,
		queryFields,
		remoteFlags,
		searchableColumns,
		// Note: query is NOT included to avoid infinite loops
		// Note: using memoized values for better performance
	]);

	const defaultColumns = [
		{
			header: 'Usuario Creación',
			accessorFn: row =>
				row?.user_info?.length
					? row.user_info[0].full_name.toString().toUpperCase()
					: '-',

			size: 'auto',
			accessorKey: 'creation_user',
			visible: false,
			dataType: 'string',
		},
		{
			header: 'Fecha Creación',
			accessorKey: 'creation_date',
			visible: false,
			dataType: 'datetime',
		},
		{
			header: 'Usuario Modificación',
			accessorFn: row =>
				row?.user_modification_info?.length
					? row.user_modification_info[0].full_name
							.toString()
							.toUpperCase()
					: '-',

			size: 'auto',
			accessorKey: 'modification_user',
			visible: false,
			dataType: 'string',
		},
		{
			header: 'Fecha Modificación',
			accessorKey: 'modification_date',
			visible: false,
			dataType: 'datetime',
		},
	];

	/**
	 * Formatea un número con separadores de miles y decimales
	 * @param {number|string} input - Número a formatear
	 * @param {number} [precision=1] - Cantidad de decimales
	 * @returns {string} Número formateado (ej: "1.234,56")
	 */
	function formatNumber(input, precision = 1) {
		let num = parseFloat(input);
		if (isNaN(num)) return '0,' + '0'.repeat(precision);
		const factor = Math.pow(10, precision);
		num = Math.round(num * factor) / factor; // round to given precision
		let [integerPart, decimalPart] = num.toFixed(precision).split('.');
		integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
		return `${integerPart},${decimalPart}`;
	}

	/**
	 * Convierte un número formateado de vuelta a número
	 * @param {string} formattedNum - Número formateado (ej: "1.234,56")
	 * @returns {number} Número parseado
	 */
	function parseFormattedNumber(formattedNum) {
		// Reemplazar puntos (separadores de miles) y cambiar la coma por punto (decimal)
		let normalizedNum = formattedNum.replace(/\./g, '').replace(',', '.');
		return parseFloat(normalizedNum);
	}

	const columnsConfig = useMemo(() => {
		let columnVisibility = {};

		const allColumns =
			Array.isArray(columns) && Array.isArray(defaultColumns)
				? [...columns, ...defaultColumns]
				: [];

		const config = allColumns.map(column => {
			let columnConfig = { ...column };
			let dataType = column?.dataType || 'string';

			switch (dataType) {
				case 'string':
					columnConfig = {
						...columnConfig,
						filterVariant: 'text',
						filterFn: 'containsWithNullValues',
						Cell: ({ cell }) =>
							cell.getValue()
								? toSmartTitleCase(cell.getValue().toString())
								: '-',
					};
					break;
				case 'number':
					columnConfig = {
						...columnConfig,
						filterVariant: 'range',
						filterFn: 'betweenInclusiveWithNullValues',
						mantineTableBodyCellProps: {
							style: { textAlign: 'right' },
						},
						mantineTableHeadCellProps: {
							style: { textAlign: 'right' },
						},
					};
					break;
				case 'datetime':
				case 'date':
					columnConfig = {
						...columnConfig,
						filterVariant: 'date-range',
						filterFn: 'betweenDates',
						Cell: ({ cell }) => {
							const date = cell.getValue();
							return date
								? dayjs(date).format(
										dataType === 'datetime'
											? 'DD-MM-YYYY HH:mm'
											: 'DD-MM-YYYY'
								  )
								: '-';
						},
						sortingFn: (rowA, rowB, id) =>
							dayjs(rowA.original[id]).diff(dayjs(rowB.original[id])),
					};
					break;
				case 'boolean':
					columnConfig = {
						...columnConfig,
						filterVariant: 'checkbox',
						filterFn: 'equalsWithNullValues',
					};
					break;
				case 'separator':
					columnConfig = {
						...columnConfig,
						filterVariant: 'range',
						filterFn: 'betweenInclusiveWithNullValues',
						Cell: ({ cell }) => {
							if (cell.getValue()) {
								return formatNumber(cell.getValue());
							}
							return '-';
						},
						mantineTableBodyCellProps: {
							style: { textAlign: 'right' },
						},
						mantineTableHeadCellProps: {
							style: { textAlign: 'right' },
						},
					};
					break;
				case 'select':
					columnConfig = {
						...columnConfig,
						filterVariant: 'select',
						filterFn: 'equalsWithNullValues',
						Cell: ({ cell }) =>
							cell.getValue()
								? toSmartTitleCase(cell.getValue().toString())
								: '-',
						...(remoteFlags.filtering && {
							Filter:
								column.Filter ||
								((props) => (
									<CustomFilterSelect
										getDataFunc={getDataFunc}
										column={props.column}
										header={props.header}
										queryPopulate={queryPopulate}
										type='select'
									/>
								)),
						}),
					};
					break;
				case 'multi-select':
					columnConfig = {
						...columnConfig,
						filterVariant: 'multi-select',
						Cell: ({ cell }) =>
							cell.getValue()
								? toSmartTitleCase(cell.getValue().toString())
								: '-',
						...(remoteFlags.filtering && {
							Filter:
								column.Filter ||
								((props) => (
									<CustomFilterSelect
										getDataFunc={getDataFunc}
										column={props.column}
										header={props.header}
										queryPopulate={queryPopulate}
										type='multi-select'
									/>
								)),
						}),
					};
					break;
				default:
					if (dataType.startsWith('float')) {
						let precision = dataType.split('.')[1] || 2;
						columnConfig = {
							...columnConfig,
							filterVariant: 'range',
							filterFn: 'betweenInclusiveWithNullValues',
							Cell: ({ cell }) => {
								const value = cell.getValue();
								if (value !== null) {
									return formatNumber(value, precision);
								}
								return '-';
							},
						};
					}
					columnConfig = {
						...columnConfig,
						Cell: ({ cell }) =>
							cell.getValue()
								? toSmartTitleCase(cell.getValue().toString())
								: '-',
					};
					break;
			}

			// 👉 CELL
			if (column?.Cell) {
				columnConfig.Cell = column.Cell;
			}

			// 👉 UPPERCASE, LOWERCASE, TITLECASE
			if (column?.uppercase || column?.lowercase || column?.titlecase) {
				columnConfig.Cell = ({ cell }) => {
					const val = cell.getValue();
					if (typeof val === 'string') {
						if (column.uppercase) {
							return val.toUpperCase();
						} else if (column.lowercase) {
							return val.toLowerCase();
						} else if (column.titlecase) {
							return toSmartTitleCase(val);
						}
					}
					return val;
				};
			}

			// 👉 ALIGN
			if (column?.align) {
				columnConfig.mantineTableBodyCellProps = {
					align: column.align,
				};
				columnConfig.mantineTableHeadCellProps = {
					align: column.align,
				};
			}else{
				columnConfig.mantineTableBodyCellProps = {
					align: 'center',
				};
				columnConfig.mantineTableHeadCellProps = {
					align: 'center',
				};
			}

			// 👉 DEFAULT SORT
			if (column?.defaultSort) {
				setSortingList([
					{ id: column.id || column.accessorKey, desc: column.defaultSort === 'desc' },
				]);
			}

			// 👉 FOOTER DE SUMA
			if (column?.summary) {
				columnConfig.Footer = ({ table }) => {
					const total = table.getRowModel().rows.reduce((acc, row) => {
						const val = row.getValue(column.accessorKey);
						return acc + (parseFloat(val) || 0);
					}, 0);
					return formatNumber(total);
				};

				// Alinear a la derecha
				columnConfig.mantineTableFooterCellProps = {
					style: { textAlign: 'right' },
				};
			}

			// 👉 VISIBLE
			if (column?.visible !== undefined) {
				columnVisibility[column.accessorKey] = column.visible;
			}

			// 👉 WIDTH
			if (column?.width) {
				if (
					typeof column.width === 'string' &&
					column.width.endsWith('%')
				) {
					columnConfig.size = Math.round(
						window.innerWidth * (parseFloat(column.width) / 100)
					);
				} else {
					columnConfig.size = column.width;
				}
			}

			// 👉 FORMAT HEADER
			if (formatHeaders && column.header) {
				columnConfig.header = toSmartTitleCase(column.header);
			}

			return columnConfig;
		});

		setColumnVisibilityList(columnVisibility);
		return config;
	}, [columns]);

	const table = useMantineReactTable({
		columns: columnsConfig,
		data: tableMode === 'local' ? data : remoteData?.data || [],
		defaultColumn: { minSize: 30, maxSize: 1000, size: 100 }, //units are in px (these are the default values)
		enableBottomToolbar: theme !== 'minimal',
		enableColumnFilters: filtering,
		enableColumnOrdering: columnOrdering,
		enableFacetedValues: !remoteFlags.filtering || false,
		enableFilterMatchHighlighting: true,
		enableGlobalFilter: searching,
		enableRowSelection: selection
			? row => enableRowSelection?.(row) ?? true
			: false,
		enableSorting: sorting,
		enableStickyFooter,
		enableStickyHeader,
		enableTopToolbar: theme !== 'minimal',
		filterFns: {
			betweenDates: (row, id, filterValue) => {
				const rowDate = dayjs(row.getValue(id));
				const [start, end] = filterValue || [];
				if (!start || !end) return true; // return if range not exists
				const startDate = dayjs(start).startOf('day');
				const endDate = dayjs(end).endOf('day');
				return (
					rowDate.isSameOrAfter(startDate) &&
					rowDate.isSameOrBefore(endDate)
				);
			},
			betweenInclusiveWithNullValues: (row, id, filterValue) =>
				MRT_FilterFns.betweenInclusive(
					{ ...row, getValue: id => row.getValue(id) ?? 0 },
					id,
					filterValue
				),
			containsWithNullValues: (row, id, filterValue) =>
				MRT_FilterFns.contains(
					{ ...row, getValue: id => row.getValue(id) ?? '' },
					id,
					filterValue
				),
			equalsWithNullValues: (row, id, filterValue) =>
				MRT_FilterFns.equals(
					{ ...row, getValue: id => row.getValue(id) ?? '' },
					id,
					filterValue
				),
		},
		getRowId: originalRow => getRowIdTable?.(originalRow) ?? null,
		initialState: {
			pagination: {
				pageSize: pagination.pageSize,
			},
		},
		localization: MRT_Localization_ES,
		manualFiltering: remoteFlags.filtering || false,
		manualPagination: remoteFlags.pagination || false,
		manualSorting: remoteFlags.sorting || false,
		mantineFilterMultiSelectProps: {
			styles: {
				dropdown: { width: 'auto !important' },
			},
		},
		mantineFilterSelectProps: {
			styles: {
				dropdown: { width: 'auto !important' },
			},
		},
		mantineProgressProps: ({ isTopToolbar }) => ({
			sx: {
				display: isTopToolbar ? 'block' : 'none',
			},
		}),
		mantineTableContainerProps,
		mantineTableProps: {
			withColumnBorders: withColumnBorders,
			...(theme === 'minimal'
				? { highlightOnHover: false }
				: { striped: true }),
		},
		mantineToolbarAlertBannerProps: error
			? {
					color: 'red',
					children: 'Error al cargar los datos',
			  }
			: undefined,
		onColumnFiltersChange: setFilteringList,
		onColumnVisibilityChange: setColumnVisibilityList,
		onDensityChange: setDensity,
		...(searching &&
			remoteFlags.filtering && {
				onGlobalFilterChange: setGlobalFilter,
			}),
		onPaginationChange: updater => {
			setPagination(updater);
			onPaginationChangeFromParent?.(updater);
		},
		onRowSelectionChange: selection?.setRowSelection,
		onSortingChange: setSortingList,
		renderToolbarInternalActions: ({ table }) => (
			<InternalToolbar
				table={table}
				mode={tableMode}
				getDataFunc={getDataFunc}
				refetchHook={refetch}
				query={query}
				columns={columnsConfig}
				nameFile={nameFile}
				clearCacheFunc={clearCacheFunc}
				exportDateField={exportDateField}
			/>
		),
		...(remoteFlags.pagination && {
			rowCount: remoteData?.meta?.totalCount || 0,
		}),
		...(masterDetailComponent && {
			renderDetailPanel: masterDetailComponent,
		}),
		selectAllMode,
		state: {
			sorting: sortingList,
			columnFilters: filteringList,
			density,
			pagination,
			columnVisibility: columnVisibilityList,
			showAlertBanner: !!error,
			showProgressBars: isFetching || isLoading || (data && isLoadingData),
			...(selection && { rowSelection: selection.rowSelection }),
			...(searching &&
				remoteFlags.filtering && {
					globalFilter,
				}),
		},
		...(customToolbarActions && {
			renderTopToolbarCustomActions: ({ table }) => {
				if (typeof customToolbarActions === 'function') {
					return customToolbarActions({ table });
				}
			
				if (Array.isArray(customToolbarActions)) {
					return (
						<div className='d-flex justify-content-start gap-2'>
							{customToolbarActions.map((action, index) => (
								<Tooltip key={index} label={action.title || action.label} position='top' withinPortal>
									<button
										className={`btn d-flex align-items-center btn-tooltip-hover ${action.className} `}
										onClick={action.onClick}
										disabled={action.disabled}
									>
										{action.icon && <i className={`${action.icon} label-icon icon-tooltop-size`}></i>}
										{action.icon && action.label && ' '}
										{action.label}
									</button>
								</Tooltip>
							))}
						</div>
					);
				}
				
				return null;
			}
		}),
		...tableExtraProps,
	});

	return (
		<>
			<style>
				{`
					.mantine-Table-root tbody > tr > td {
						padding: .313rem .625rem !important;
					}
					.mantine-Table-root tbody > tr > td.mantine-TableBodyCell-DetailPanel {
						padding: 0 !important;
					}
				`}
			</style>
			<MantineReactTable table={table} />
		</>
	);
};

/**
 * InternalToolbar - Barra de herramientas interna para exportación y filtros
 *
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.table - Instancia de la tabla de Mantine React Table
 * @param {string} props.mode - Modo de operación ('local' | 'remote')
 * @param {Function} props.getDataFunc - Función para obtener datos del backend
 * @param {Function} props.refetchHook - Refetch remote data
 * @param {Object} props.query - Query actual de la tabla
 * @param {Array} props.columnsn - Configuración de columnas (nombre con typo, mantener compatibilidad)
 * @param {string} [props.nameFile] - Nombre del archivo para exportación
 * @param {Function} props.clearCacheFunc - Función para limpiar el cache
 * @param {string} [props.exportDateField='creation_date'] - Campo de fecha a usar en el filtro de exportación
 *
 * @returns {JSX.Element} Toolbar con botones de exportación y filtros
 */
const InternalToolbar = ({
	table,
	mode,
	getDataFunc,
	refetchHook,
	query,
	columns,
	nameFile,
	clearCacheFunc,
	exportDateField = 'creation_date',
}) => {
	const [exportQuery, setExportQuery] = useState();
	const [opened, { open, close, toggle }] = useDisclosure(false);
	const dispatch = useDispatch();

	const {
		data: remoteData,
		error,
		isLoading,
		isFetching,
	} = useFetchRemoteData(getDataFunc, {
		query: exportQuery,
		skip: mode === 'local',
	});

	/**
	 * Extrae texto de un objeto React (JSX) de forma recursiva
	 * @param {any} reactObject - Objeto React del que extraer texto
	 * @returns {string} Texto extraído
	 */
	const extractTextFromReactObject = reactObject => {
		if (typeof reactObject === 'string') return reactObject.trim();
		if (typeof reactObject === 'number') return reactObject.toString();
		if (typeof reactObject === 'separator') return reactObject.toString();

		const children = reactObject?.props?.children;
		if (!children) return '';

		return Array.isArray(children)
			? children.map(extractTextFromReactObject).join(' ').trim()
			: extractTextFromReactObject(children);
	};

	/**
	 * Maneja la exportación de filas locales a Excel
	 * @param {Array} rows - Filas de la tabla a exportar
	 */
	const handleExportLocalRows = rows => {
		const rowData = rows.map(row => {
			const formattedRow = {};

			row.getVisibleCells().forEach(cell => {
				if (cell.column.columnDef.columnDefType === 'data') {
					const { header, Cell, accessorFn, accessorKey, dataType } =
						cell.column.columnDef;
					let cellValue = null;

					if (Cell) {
						const cellRenderResult = Cell({ cell, row: cell.row });
						cellValue = extractTextFromReactObject(cellRenderResult);
					}

					if (!cellValue && accessorFn) {
						cellValue = accessorFn(cell.row.original);
					}

					if (!cellValue && accessorKey) {
						cellValue = cell.getValue();
					}

					cellValue = cellValue || '-';

					if (
						dataType === 'number' ||
						(dataType === 'separator' && cellValue !== '-')
					) {
						if (dataType === 'separator') {
							cellValue = parseFormattedNumber(cellValue);
						}
						const numericValue = parseFloat(cellValue);
						if (!isNaN(numericValue)) {
							cellValue = numericValue;
						}
					}

					formattedRow[header] = cellValue;
				}
			});

			return formattedRow;
		});

		generateExcel(rowData, nameFile);
	};

	/**
	 * Convierte un número formateado de vuelta a número (versión local del InternalToolbar)
	 * @param {string} formattedNum - Número formateado (ej: "1.234,56")
	 * @returns {number} Número parseado
	 */
	function parseFormattedNumber(formattedNum) {
		let normalizedNum = formattedNum.replace(/\./g, '').replace(',', '.');
		return parseFloat(normalizedNum);
	}

	// when is a remote table, we need to fetch all data to export
	useEffect(() => {
		if (mode === 'remote' && remoteData) {
			const tableToExport =
				remoteData.data?.map(row =>
					columns
						.filter(
							column =>
								column.visible !== false &&
								column.columnDefType !== 'display'
						)
						.reduce((acc, column) => {
							const value =
								column.accessorKey && column.accessorKey.includes('.')
									? column.accessorKey
											.split('.')
											.reduce((acc, key) => {
												return acc[key];
											}, row)
									: column.accessorKey
									? row[column.accessorKey]
									: column.accessorFn
									? column.accessorFn(row)
									: '';

							acc[column.header] = value;
							return acc;
						}, {})
				) || [];

			if (!tableToExport.length) {
				tableToExport.push(
					columns.reduce((acc, column) => {
						acc[column.header] = '';
						return acc;
					}, {})
				);
			}

			generateExcel(tableToExport);
			close();
		}
	}, [remoteData]);

	const form = useForm();

	return (
		<>
			<MRT_ToggleGlobalFilterButton table={table} />
			{(mode === 'remote' || clearCacheFunc) && (
				<ActionIcon
					size={21}
					aria-label='Refrescar'
					onClick={() => {
						if (mode === 'remote') {
							dispatch(
								getDataFunc({
									clear_cache: true,
									pagination: {
										page: 1,
										pageSize: 1,
									},
								})
							).then(res => {
								if (res.type.endsWith('fulfilled')) {
									refetchHook();
								}
							});
						} else {
							clearCacheFunc();
						}
					}}
				>
					<Tooltip label='Refrescar' position='top' withinPortal>
						<IconRefresh />
					</Tooltip>
				</ActionIcon>
			)}
			<Menu position='bottom-end' shadow='md' withArrow withinPortal>
				<Menu.Target>
					<ActionIcon size={21} aria-label='Exportar a Excel'>
						<Tooltip label='Exportar a Excel' position='top' withinPortal>
							<IconTableExport />
						</Tooltip>
					</ActionIcon>
				</Menu.Target>
				<Menu.Dropdown>
					<Menu.Item
						disabled={table.getRowModel().rows.length === 0}
						onClick={() =>
							handleExportLocalRows(table.getRowModel().rows)
						}
					>
						Exportar esta Página
					</Menu.Item>
					<Menu.Item
						disabled={table.getRowModel().rows.length === 0}
						onClick={() =>
							mode === 'local'
								? handleExportLocalRows(
										table.getPrePaginationRowModel().rows
								  )
								: open()
						}
					>
						Exportar toda la Tabla
					</Menu.Item>
				</Menu.Dropdown>
			</Menu>
			<MRT_ToggleFiltersButton table={table} />
			<MRT_ShowHideColumnsButton table={table} />
			{/* <MRT_ToggleDensePaddingButton table={table} /> */}
			{/* <MRT_ToggleFullScreenButton table={table} /> */}
			<Modal
				// title='Seleccione rango de fechas de creación actividad para exportar'
				isOpen={opened}
				toggle={toggle}
				size='sm'
				className='border-0'
				modalClassName='modal fade zoomIn'
				backdrop={'static'}
				centered={true}
			>
				<ModalHeader className='modal-title' />
				<ModalBody className='text-center p-3'>
					<div>
						<h4 className='mb-3'>
							Seleccione rango de fechas de creación actividad para
							exportar
						</h4>
						<form
							onSubmit={form.onSubmit(values => {
								let queryFilter = {};

								if (values.start && values.end) {
									queryFilter[exportDateField] = {
										$between: [
											dayjs
												.utc(values.start)
												.startOf('day')
												.toISOString(),
											dayjs
												.utc(values.end)
												.startOf('day')
												.toISOString(),
										],
									};
								}

								if (values.start && !values.end) {
									queryFilter[exportDateField] = {
										$gte: dayjs
											.utc(values.start)
											.startOf('day')
											.toISOString(),
									};
								}

								if (!values.start && values.end) {
									queryFilter[exportDateField] = {
										$lte: dayjs
											.utc(values.end)
											.startOf('day')
											.toISOString(),
									};
								}

								setExportQuery({
									...query,
									filters: {
										...query.filters,
										...queryFilter,
									},
									pagination: {
										page: 1,
										pageSize: 10000,
									},
								});
							})}
						>
							<Stack>
								<Group noWrap>
									<DateInput
										size='xs'
										label='Fecha Inicial'
										{...form.getInputProps('start')}
									/>
									<DateInput
										size='xs'
										label='Fecha Final'
										{...form.getInputProps('end')}
									/>
								</Group>
								<div className='hstack gap-2 justify-content-center'>
									<Button
										className='btn-danger btn-label'
										onClick={close}
										style={{ width: '110px' }}
									>
										<i className='icon-x label-icon'></i>{' '}
										Cancelar
									</Button>
									<button
										className='btn btn-label btn-primary'
										type='submit'
										style={{ width: '110px' }}
										disabled={form.isDirty() === false || isFetching}
									>
										{isFetching || isLoading ? (
											<i className='label-icon'>
												<Spinner size='sm' />
											</i>
										) : (
											<i className={`${Icons.check2} label-icon`}></i>
										)}{' '}
										Confirmar
									</button>
								</div>
								{error && error.message?.slice(-3) === '400' && (
									<Alert
										title='Error, puede que la consulta sea demaciado grande'
										color='red'
									>
										Intente con un rango más pequeño
									</Alert>
								)}
							</Stack>
						</form>
					</div>
				</ModalBody>
			</Modal>
		</>
	);
};
